import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Divider,
  Input,
  Popconfirm,
  Space,
  Switch,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import ProductSelector from "../../components/proposals/ProductSelector";
import ItemMetadataManager from "../../components/proposals/ItemMetadataManager";
import ItemImageUpload from "../../components/proposals/ItemImageUpload";
import ItemPricing from "../../components/proposals/ItemPricing";
import PieceSelector from "../../components/PieceSelector";
import { formatPrice } from "../../utils/currencyUtils";
import axios from "../../services/axiosInstance";

const { TextArea } = Input;
const { Text: TypographyText } = Typography;

const ProposalItem = ({
  index,
  item,
  products,
  languages = [],
  proposalCurrency = 'CZK',
  handleItemChange,
  handleItemBlur,
  handleDeleteItem,
  handleDrop,
  handleRemoveImage,
  handleReorderImages,
  handleUpdateImageDescription,
  proposalId = null,
  validationError = null,
}) => {
  const [isCustom, setIsCustom] = useState(item.is_custom);
  const [pieces, setPieces] = useState(item.pieces || []);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [itemMetadata, setItemMetadata] = useState(item.item_metadata || []);
  const [originalProductData, setOriginalProductData] = useState(null);
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  // Sync local state with item prop changes
  useEffect(() => {
    setIsCustom(item.is_custom);
  }, [item.is_custom]);

  useEffect(() => {
    setPieces(item.pieces || []);
  }, [item.pieces]);

  useEffect(() => {
    setItemMetadata(item.item_metadata || item.metadata || []);
  }, [item.item_metadata, item.metadata]);

  const handleProductSelect = async (selected) => {
    if (selected) {
      const updatedFields = {
        product_id: selected.value,
        item_name: selected.product.name,
        is_custom: false,
      };

      // Only set list_price if it's currently empty or 0 (preserve user input)
      if (!item.list_price || item.list_price === 0 || item.list_price === '0') {
        updatedFields.list_price = selected.product.base_price;
      }

      // Store original product data for restoration
      setOriginalProductData({
        product_id: selected.value,
        item_name: selected.product.name,
        base_price: selected.product.base_price
      });

      // Update all fields at once
      Object.entries(updatedFields).forEach(([field, value]) => {
        handleItemChange(index, field, value);
      });

      // Update local state to match form data
      setIsCustom(false);

      // Clear pieces when product changes
      setPieces([]);
      handleItemChange(index, "pieces", []);

      // Load product details including pieces and metadata
      try {
        const response = await axios.get(`/products/${selected.value}`);
        const productData = response.data;
        setSelectedProduct(productData);
        
        // Load product metadata and set it as item metadata
        if (productData.metadata && Array.isArray(productData.metadata)) {
          const productMetadata = productData.metadata.map(meta => ({
            key_name: meta.key_name,
            value: meta.value || "",
            is_mandatory: meta.is_mandatory || false
          }));
          setItemMetadata(productMetadata);
          handleItemChange(index, "item_metadata", productMetadata);
        }
      } catch (error) {
        console.error('Error loading product details:', error);
      }
    }
  };


  const toggleCustom = () => {
    const newIsCustom = !isCustom;
    console.log('Toggle custom:', { 
      currentIsCustom: isCustom, 
      newIsCustom, 
      currentProductId: item.product_id, 
      currentItemName: item.item_name,
      originalProductData
    });
    
    setIsCustom(newIsCustom);
    handleItemChange(index, "is_custom", newIsCustom);
    
    if (newIsCustom) {
      // When switching TO custom mode, clear product_id but keep item_name
      console.log('Switching TO custom mode - clearing product_id');
      handleItemChange(index, "product_id", null);
    } else {
      // When switching FROM custom mode, restore original product data
      console.log('Switching FROM custom mode', { 
        originalProductData,
        currentProductId: item.product_id
      });
      
      if (originalProductData && !item.product_id) {
        console.log('Restoring original product data:', originalProductData);
        handleItemChange(index, "product_id", originalProductData.product_id);
        handleItemChange(index, "item_name", originalProductData.item_name);
      }
    }
  };

  const handlePiecesChange = (newPieces) => {
    setPieces(newPieces);
    handleItemChange(index, "pieces", newPieces);
  };

  const handleMetadataAdd = () => {
    const newMetadata = [...itemMetadata, { key_name: "", value: "" }];
    setItemMetadata(newMetadata);
    handleItemChange(index, "item_metadata", newMetadata);
  };

  const handleMetadataChange = (metadataIndex, field, value) => {
    const newMetadata = [...itemMetadata];
    newMetadata[metadataIndex][field] = value;
    setItemMetadata(newMetadata);
    handleItemChange(index, "item_metadata", newMetadata);
  };

  const handleMetadataDelete = (metadataIndex) => {
    const newMetadata = itemMetadata.filter((_, i) => i !== metadataIndex);
    setItemMetadata(newMetadata);
    handleItemChange(index, "item_metadata", newMetadata);
  };

  const itemSummary = item.item_name || "Select product...";
  const quantity = item.quantity || 1;
  const price = item.final_price ?? item.list_price ?? 0;

  return (
    <Card
      size="small"
      data-testid={`item-${index}`}
      title={
        <TypographyText strong ellipsis style={{ maxWidth: 280 }}>
          {item.item_name || "Untitled item"}
        </TypographyText>
      }
      extra={
        <Space size="middle">
          <Space size={6}>
            <TypographyText type="secondary" style={{ fontSize: 12 }}>
              Custom
            </TypographyText>
            <Switch
              size="small"
              checked={isCustom}
              onChange={toggleCustom}
              aria-label="Custom Item"
            />
          </Space>
          <Popconfirm
            title="Delete this item?"
            description="This cannot be undone."
            okText="Yes, delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteItem(index)}
          >
            <Button
              danger
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              data-testid={`remove-item-${index}`}
            />
          </Popconfirm>
        </Space>
      }
      styles={{
        body: { paddingTop: 12 },
        header: { minHeight: 44 },
      }}
      style={{
        marginBottom: 0,
        borderColor: validationError ? "#ff4d4f" : undefined,
      }}
    >
      {validationError && (
        <Alert
          type="error"
          showIcon
          message={validationError}
          style={{ marginBottom: 12 }}
        />
      )}

      <ProductSelector
        item={item}
        proposalCurrency={proposalCurrency}
        onProductSelect={handleProductSelect}
        isCustom={isCustom}
        onItemChange={handleItemChange}
        index={index}
      />

      <Divider style={{ margin: "12px 0" }} />

      <ItemPricing
        item={item}
        proposalCurrency={proposalCurrency}
        onItemChange={handleItemChange}
        onItemBlur={handleItemBlur}
        index={index}
      />

      <Collapse
        size="small"
        style={{ marginTop: 12 }}
        activeKey={detailsExpanded ? ["details"] : []}
        onChange={(keys) =>
          setDetailsExpanded(
            Array.isArray(keys) ? keys.includes("details") : keys === "details"
          )
        }
        items={[
          {
            key: "details",
            showArrow: false,
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {detailsExpanded ? <DownOutlined /> : <RightOutlined />}
                <span>{detailsExpanded ? "Hide details" : "More details"}</span>
                {!detailsExpanded && (
                  <TypographyText type="secondary" style={{ fontSize: 12 }}>
                    {itemSummary} × {quantity}
                    {price ? ` — ${formatPrice(price, proposalCurrency)}` : ""}
                  </TypographyText>
                )}
              </span>
            ),
            children: (
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <div>
                  <TypographyText
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                  >
                    PDF description (optional)
                  </TypographyText>
                  <TextArea
                    rows={2}
                    placeholder="Shown in the product summary PDF..."
                    value={item.custom_description || ""}
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        "custom_description",
                        e.target.value
                      )
                    }
                    showCount
                    maxLength={2000}
                  />
                </div>

                <ItemImageUpload
                  item={item}
                  index={index}
                  onDrop={handleDrop}
                  onRemoveImage={handleRemoveImage}
                  onReorderImages={handleReorderImages}
                  onUpdateImageDescription={handleUpdateImageDescription}
                />

                <ItemMetadataManager
                  itemMetadata={itemMetadata}
                  onMetadataAdd={handleMetadataAdd}
                  onMetadataChange={handleMetadataChange}
                  onMetadataDelete={handleMetadataDelete}
                  selectedProduct={selectedProduct}
                />

                <PieceSelector
                  product={selectedProduct}
                  selectedPieces={pieces}
                  onPiecesChange={handlePiecesChange}
                  proposalId={proposalId}
                  proposalItemId={item.tempKey || item.proposal_item_id}
                />
              </Space>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default ProposalItem;
